import {FC, useState} from 'react'
import {api, PayoutRequest} from '../../services/api'
import {usePaginated} from '../../hooks/usePaginated'
import {Pagination} from '../../components/Pagination'
import {toast} from '../../utils/toast'

const STATUS_META: Record<string, {label: string; bg: string; color: string}> = {
  pending:  {label: 'Pending',  bg: '#fef9c3', color: '#ca8a04'},
  approved: {label: 'Approved', bg: '#e0f2fe', color: '#0369a1'},
  paid:     {label: 'Paid',     bg: '#dcfce7', color: '#16a34a'},
  rejected: {label: 'Rejected', bg: '#f3f4f6', color: '#6b7280'},
}
const STATUS_OPTIONS = ['pending', 'approved', 'paid', 'rejected']

// What an admin may do next. A request that has been refused or sent is
// finished with — reopening it would let the same money be approved twice.
const DECISIONS: Record<string, string[]> = {
  pending: ['approved', 'rejected'],
  approved: ['paid', 'rejected'],
  paid: [],
  rejected: [],
}

const fmtDate = (iso: string | null): string => {
  if (!iso) return '—'
  const d = new Date(iso)
  return isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})
}

const money = (value: number | string | null | undefined): string =>
  `$${(Number(value) || 0).toFixed(2)}`

const PayoutsPage: FC = () => {
  const [statusFilter, setStatusFilter] = useState('all')
  const [saving, setSaving] = useState(false)

  const {items: payouts, total, page, setPage, search, setSearch, limit, loading, error, reload} =
    usePaginated<PayoutRequest>(
      (p, l, s) => api.getPayoutsPaged(p, l, s, {status: statusFilter !== 'all' ? statusFilter : undefined}),
      {limit: 20, deps: [statusFilter]}
    )

  const [target, setTarget] = useState<PayoutRequest | null>(null)
  const [fStatus, setFStatus] = useState('')
  const [fNote, setFNote] = useState('')
  const [formError, setFormError] = useState('')

  const openManage = (p: PayoutRequest) => {
    setTarget(p)
    // Pre-set to the obvious next step rather than to where it already is,
    // so saving without touching the dropdown does something sensible.
    setFStatus(DECISIONS[p.status]?.[0] ?? p.status)
    setFNote(p.adminNote ?? '')
    setFormError('')
  }

  const handleSave = async () => {
    if (!target) return
    setSaving(true); setFormError('')
    try {
      await api.updatePayout(target.id, {status: fStatus, adminNote: fNote})
      setTarget(null)
      reload()
      toast(
        fStatus === 'paid' ? 'Marked as sent'
        : fStatus === 'approved' ? 'Withdrawal approved'
        : fStatus === 'rejected' ? 'Withdrawal declined'
        : 'Withdrawal updated'
      )
    } catch (e: any) {
      setFormError(e.message ?? 'Failed to save.')
      toast(e?.message ?? 'Update failed', 'error')
    } finally {
      setSaving(false)
    }
  }

  const inputStyle = {width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, color: '#0f172a', background: '#f8fafc', outline: 'none', boxSizing: 'border-box' as const}
  const labelStyle = {fontSize: 13, fontWeight: 600 as const, color: '#374151', display: 'block' as const, marginBottom: 6}
  const rowStyle = {display: 'flex', justifyContent: 'space-between', padding: '6px 0'}

  const StatusBadge: FC<{status: string}> = ({status}) => {
    const m = STATUS_META[status] ?? STATUS_META.pending
    return <span style={{padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: m.bg, color: m.color}}>{m.label}</span>
  }

  return (
    <div>
      <div style={{marginBottom: 24}}>
        <h1 style={{fontSize: 24, fontWeight: 700, color: '#0f172a', margin: '0 0 4px'}}>Withdrawals</h1>
        <p style={{fontSize: 14, color: '#64748b', margin: 0}}>
          Drivers asking for their earnings — approve, then mark Paid once the money has been sent
        </p>
      </div>

      <div style={{background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '16px 20px', marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', display: 'flex', gap: 12, alignItems: 'center'}}>
        <div style={{position: 'relative', flex: 1}}>
          <svg style={{position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8'}} width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
            <circle cx='11' cy='11' r='8'/><line x1='21' y1='21' x2='16.65' y2='16.65'/>
          </svg>
          <input type='text' placeholder='Search by reference...' value={search}
            onChange={e => setSearch(e.target.value)} style={{...inputStyle, paddingLeft: 38, background: '#fff'}} />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{...inputStyle, width: 160, background: '#fff'}}>
          <option value='all'>All statuses</option>
          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
        </select>
        <button onClick={reload} disabled={loading}
          style={{display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, fontWeight: 500, color: '#374151', background: '#f8fafc', cursor: 'pointer'}}>
          <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' style={{animation: loading ? 'spin 1s linear infinite' : 'none'}}>
            <polyline points='23 4 23 10 17 10'/><polyline points='1 20 1 14 7 14'/><path d='M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15'/>
          </svg>
          Refresh
        </button>
      </div>

      {error && (
        <div style={{padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 14, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <span>{error}</span>
          <button onClick={reload} style={{color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500, fontSize: 13}}>Retry</button>
        </div>
      )}

      <div style={{background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden'}}>
        {loading ? (
          <div style={{display: 'flex', justifyContent: 'center', padding: '60px 0'}}>
            <div style={{width: 32, height: 32, border: '3px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite'}} />
          </div>
        ) : payouts.length === 0 ? (
          <div style={{padding: '60px 20px', textAlign: 'center', color: '#94a3b8', fontSize: 14}}>
            {statusFilter === 'all' ? 'No withdrawal requests yet.' : `No ${STATUS_META[statusFilter]?.label.toLowerCase()} requests.`}
          </div>
        ) : (
          <div style={{overflowX: 'auto'}}>
            <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 14}}>
              <thead>
                <tr style={{background: '#f8fafc', borderBottom: '1px solid #e2e8f0'}}>
                  {['REF', 'DRIVER', 'AMOUNT', 'STATUS', 'REQUESTED', 'DECIDED', 'ACTIONS'].map(h => (
                    <th key={h} style={{padding: '11px 20px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {payouts.map((p, i) => (
                  <tr key={p.id} style={{borderBottom: i < payouts.length - 1 ? '1px solid #f1f5f9' : 'none'}}
                    onMouseEnter={ev => (ev.currentTarget.style.background = '#fafafa')}
                    onMouseLeave={ev => (ev.currentTarget.style.background = 'transparent')}>
                    <td style={{padding: '14px 20px', fontFamily: 'monospace', fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap'}}>{p.reference}</td>
                    <td style={{padding: '14px 20px'}}>
                      {p.driver
                        ? <div>
                            <div style={{fontWeight: 600, color: '#0f172a'}}>{p.driver.name}</div>
                            <div style={{fontSize: 12, color: '#94a3b8'}}>{p.driver.email || p.driver.phone || '—'}</div>
                          </div>
                        : <span style={{color: '#94a3b8'}}>—</span>}
                    </td>
                    <td style={{padding: '14px 20px', fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap'}}>{money(p.amount)}</td>
                    <td style={{padding: '14px 20px'}}><StatusBadge status={p.status} /></td>
                    <td style={{padding: '14px 20px', color: '#64748b', whiteSpace: 'nowrap'}}>{fmtDate(p.createdAt)}</td>
                    <td style={{padding: '14px 20px', color: '#64748b', whiteSpace: 'nowrap'}}>{fmtDate(p.processedAt)}</td>
                    <td style={{padding: '14px 20px'}}>
                      <button onClick={() => openManage(p)}
                        style={{padding: '6px 14px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, fontWeight: 500, color: '#374151', background: '#fff', cursor: 'pointer', whiteSpace: 'nowrap'}}>
                        {DECISIONS[p.status]?.length ? 'Review' : 'View'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pagination page={page} limit={limit} total={total} onPageChange={setPage} />
      </div>

      {target && (
        <div style={{position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, zIndex: 1000}}
          onClick={() => (saving ? undefined : setTarget(null))}>
          <div style={{background: '#fff', borderRadius: 12, width: '100%', maxWidth: 520, padding: 24, boxShadow: '0 20px 40px rgba(0,0,0,0.2)'}}
            onClick={e => e.stopPropagation()}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16}}>
              <h3 style={{fontSize: 18, fontWeight: 700, color: '#0f172a', margin: 0}}>Withdrawal {target.reference}</h3>
              <StatusBadge status={target.status} />
            </div>

            <div style={{background: '#f8fafc', borderRadius: 8, padding: '12px 16px', fontSize: 14, color: '#0f172a', marginBottom: 20}}>
              <div style={rowStyle}>
                <span style={{color: '#94a3b8'}}>
                  {target.payeeKind === 'business' ? 'Partner' : 'Driver'}
                </span>
                <span>{target.driver?.name ?? '—'}</span>
              </div>
              <div style={rowStyle}>
                <span style={{color: '#94a3b8'}}>Contact</span>
                <span>{target.driver?.email || target.driver?.phone || '—'}</span>
              </div>
              <div style={{...rowStyle, borderTop: '1px solid #e2e8f0', marginTop: 6, paddingTop: 10, fontWeight: 700}}>
                <span>Amount</span>
                <span>{money(target.amount)}</span>
              </div>
              <div style={rowStyle}>
                <span style={{color: '#94a3b8'}}>Requested</span>
                <span>{fmtDate(target.createdAt)}</span>
              </div>
            </div>

            {/* Where to send it. This is a manual bank transfer, so the account
                number is shown in full — it is the thing being typed into the
                banking screen. Requests made before payout details existed
                have none, and say so rather than showing a blank row. */}
            <div style={{background: '#f8fafc', borderRadius: 8, padding: '12px 16px', fontSize: 14, color: '#0f172a', marginBottom: 20}}>
              <div style={{color: '#94a3b8', fontSize: 12, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.4}}>Send funds to</div>
              {target.destination ? (
                <>
                  <div style={rowStyle}>
                    <span style={{color: '#94a3b8'}}>Account name</span>
                    <span>{target.destination.accountName ?? '—'}</span>
                  </div>
                  <div style={rowStyle}>
                    <span style={{color: '#94a3b8'}}>Bank</span>
                    <span>{target.destination.bankName ?? '—'}</span>
                  </div>
                  <div style={rowStyle}>
                    <span style={{color: '#94a3b8'}}>Account number</span>
                    <span style={{fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace'}}>
                      {target.destination.accountNumber ?? `•••• ${target.destination.last4 ?? ''}`}
                    </span>
                  </div>
                  {target.destination.routingNumber ? (
                    <div style={rowStyle}>
                      <span style={{color: '#94a3b8'}}>Routing</span>
                      <span style={{fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace'}}>
                        {target.destination.routingNumber}
                      </span>
                    </div>
                  ) : null}
                  {target.destination.removed ? (
                    <div style={{color: '#b45309', fontSize: 12, marginTop: 8}}>
                      This account has since been removed. The details above are
                      what it was when the request was made.
                    </div>
                  ) : null}
                </>
              ) : (
                <div style={{color: '#b91c1c', fontSize: 13}}>
                  No payout details on this request — ask them to add a bank
                  account and request again.
                </div>
              )}
            </div>

            {DECISIONS[target.status]?.length ? (
              <>
                <div style={{marginBottom: 16}}>
                  <label style={labelStyle}>Decision</label>
                  <select value={fStatus} onChange={e => setFStatus(e.target.value)} style={inputStyle}>
                    {DECISIONS[target.status].map(s => (
                      <option key={s} value={s}>{STATUS_META[s].label}</option>
                    ))}
                  </select>
                </div>

                <div style={{marginBottom: 16}}>
                  {/* The driver reads this, so it is written for them: why it
                      was refused, or how the money was sent. */}
                  <label style={labelStyle}>Note to the driver</label>
                  <textarea value={fNote} onChange={e => setFNote(e.target.value)} rows={3}
                    placeholder={fStatus === 'rejected' ? 'Why was this declined?' : 'How was it sent? (optional)'}
                    style={{...inputStyle, resize: 'vertical' as const}} />
                </div>
              </>
            ) : (
              <div style={{marginBottom: 16, fontSize: 14, color: '#64748b'}}>
                This request has been settled and cannot be changed.
                {target.adminNote ? <div style={{marginTop: 8, color: '#0f172a'}}>“{target.adminNote}”</div> : null}
              </div>
            )}

            {formError && (
              <div style={{padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 13, marginBottom: 16}}>{formError}</div>
            )}

            <div style={{display: 'flex', gap: 12, justifyContent: 'flex-end'}}>
              <button onClick={() => setTarget(null)} disabled={saving}
                style={{padding: '10px 18px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, fontWeight: 500, color: '#374151', background: '#fff', cursor: 'pointer'}}>
                Close
              </button>
              {DECISIONS[target.status]?.length ? (
                <button onClick={handleSave} disabled={saving}
                  style={{padding: '10px 18px', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, color: '#fff', background: saving ? '#94a3b8' : '#3b82f6', cursor: saving ? 'default' : 'pointer'}}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      )}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}

const PayoutsPageWrapper: FC = () => <PayoutsPage />

export {PayoutsPageWrapper}
